import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import SubmissionsGrading from './SubmissionsGrading';
import QuizGradingDialog from './QuizGradingDialog';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Card,
  CardContent,
  Collapse,
  useMediaQuery,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Quiz as QuizIcon,
  Description as DocumentIcon,
  TouchApp as InteractiveIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { API_BASE_URL } from '../utils/api';

interface GradebookStudent {
  id: number;
  name: string;
}

interface GradebookAssignment {
  id: string;
  type: 'quiz' | 'document';
  format: 'quiz' | 'interactive' | 'document';
  title: string;
  due_date: string | null;
  quiz_assignment_id?: number;
  assignment_id?: number;
}

interface ScoreCell {
  status: string;
  score: number | null;
  grade?: string | null;
  points_earned?: number | null;
  points_possible?: number | null;
  submission_id?: number;
  attempt_id?: number;
  time_seconds?: number | null;
}

interface GradebookData {
  success: boolean;
  class_id: number;
  class_name: string;
  students: GradebookStudent[];
  assignments: GradebookAssignment[];
  scores: Record<string, Record<string, ScoreCell>>;
  averages: Record<string, number | null>;
}

interface ClassOption {
  id: number;
  name: string;
}

interface GradebookProps {
  initialClassId?: number | null;
}

const Gradebook: React.FC<GradebookProps> = ({ initialClassId }) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<number | ''>('');
  const [data, setData] = useState<GradebookData | null>(null);
  const [loading, setLoading] = useState(false);
  const [classesLoading, setClassesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Drill-down state
  const [submissionsOpen, setSubmissionsOpen] = useState(false);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<number | null>(null);
  const [selectedDocumentName, setSelectedDocumentName] = useState('');

  const [quizGradingOpen, setQuizGradingOpen] = useState(false);
  const [selectedQuizAssignmentId, setSelectedQuizAssignmentId] = useState<number | null>(null);
  const [selectedQuizTitle, setSelectedQuizTitle] = useState('');

  // Mobile card expansion
  const [expandedStudent, setExpandedStudent] = useState<number | null>(null);

  const fetchClasses = useCallback(async () => {
    try {
      setClassesLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/classes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.success && json.classes) {
        setClasses(json.classes.map((c: any) => ({ id: c.id, name: c.name })));
      }
    } catch {
      setError(t('gradebook.errorLoadingClasses', 'Failed to load classes'));
    } finally {
      setClassesLoading(false);
    }
  }, [token, t]);

  const fetchGradebook = useCallback(async (classId: number) => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE_URL}/api/classes/${classId}/gradebook`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.success) {
        setData(json);
      } else {
        setError(json.error || t('gradebook.errorLoading', 'Failed to load gradebook'));
      }
    } catch {
      setError(t('gradebook.errorLoading', 'Failed to load gradebook'));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  useEffect(() => {
    if (initialClassId && classes.length > 0 && !selectedClassId) {
      const match = classes.find((c) => c.id === initialClassId);
      if (match) setSelectedClassId(match.id);
    }
  }, [initialClassId, classes, selectedClassId]);

  useEffect(() => {
    if (selectedClassId) {
      fetchGradebook(selectedClassId);
    } else {
      setData(null);
    }
  }, [selectedClassId, fetchGradebook]);

  const handleExportCsv = async () => {
    if (!selectedClassId) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/classes/${selectedClassId}/gradebook/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = res.headers.get('Content-Disposition');
      const match = disposition?.match(/filename="?(.+?)"?$/);
      a.download = match?.[1] || 'gradebook.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError(t('gradebook.exportError', 'Failed to export CSV'));
    }
  };

  const handleCellClick = (assignment: GradebookAssignment, cell: ScoreCell) => {
    if (assignment.type === 'quiz' && assignment.quiz_assignment_id) {
      setSelectedQuizAssignmentId(assignment.quiz_assignment_id);
      setSelectedQuizTitle(assignment.title);
      setQuizGradingOpen(true);
    } else if (assignment.type === 'document' && assignment.assignment_id) {
      setSelectedAssignmentId(assignment.assignment_id);
      setSelectedDocumentName(assignment.title);
      setSubmissionsOpen(true);
    }
  };

  const formatLabel = (fmt: GradebookAssignment['format']) => {
    switch (fmt) {
      case 'quiz': return t('gradebook.formatQuiz', 'Quiz');
      case 'interactive': return t('gradebook.formatInteractive', 'Interactive');
      default: return t('gradebook.formatDocument', 'Document');
    }
  };

  const formatColor = (fmt: GradebookAssignment['format']) => {
    switch (fmt) {
      case 'quiz': return '#5c6bc0';
      case 'interactive': return '#00897b';
      default: return '#78909c';
    }
  };

  const getScoreChip = (cell: ScoreCell | undefined, format?: GradebookAssignment['format']) => {
    if (!cell || cell.status === 'not_started') {
      return <Typography variant="body2" color="text.secondary">--</Typography>;
    }
    if (cell.status === 'in_progress' || cell.status === 'draft') {
      return (
        <Chip
          label={t('gradebook.inProgress', 'In Progress')}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.75rem' }}
        />
      );
    }
    if (cell.status === 'submitted' || cell.status === 'returned') {
      return (
        <Chip
          label={t(`gradebook.${cell.status}`, cell.status === 'submitted' ? 'Submitted' : 'Returned')}
          size="small"
          color="info"
          variant="outlined"
          sx={{ fontSize: '0.75rem' }}
        />
      );
    }
    if ((cell.status === 'graded' || cell.status === 'reviewed') && cell.score != null) {
      const color = cell.score >= 70 ? 'success' : cell.score >= 50 ? 'warning' : 'error';
      return (
        <>
          <Chip
            label={`${cell.score}%`}
            size="small"
            color={color}
            sx={{ fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}
          />
          {format && (
            <Typography variant="caption" display="block" sx={{ fontSize: '0.6rem', color: formatColor(format), fontWeight: 600, mt: 0.25 }}>
              {formatLabel(format)}
            </Typography>
          )}
        </>
      );
    }
    if ((cell.status === 'graded' || cell.status === 'reviewed') && cell.grade) {
      return (
        <>
          <Chip
            label={cell.grade}
            size="small"
            color="success"
            sx={{ fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}
          />
          {format && (
            <Typography variant="caption" display="block" sx={{ fontSize: '0.6rem', color: formatColor(format), fontWeight: 600, mt: 0.25 }}>
              {formatLabel(format)}
            </Typography>
          )}
        </>
      );
    }
    return <Typography variant="body2" color="text.secondary">--</Typography>;
  };

  const formatTime = (seconds: number | null | undefined) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const computeStats = () => {
    if (!data) return { classAvg: null, totalAssignments: 0, completionRate: null };
    const allAvgs = Object.values(data.averages).filter((a): a is number => a !== null);
    const classAvg = allAvgs.length ? Math.round(allAvgs.reduce((s, v) => s + v, 0) / allAvgs.length * 10) / 10 : null;

    let graded = 0;
    let total = 0;
    for (const sid of Object.keys(data.scores)) {
      for (const cell of Object.values(data.scores[sid])) {
        total++;
        if (cell.status === 'graded') graded++;
      }
    }
    const completionRate = total > 0 ? Math.round((graded / total) * 100) : null;
    return { classAvg, totalAssignments: data.assignments.length, completionRate };
  };

  const stats = data ? computeStats() : null;

  // Mobile card-based view
  const renderMobileView = () => {
    if (!data) return null;
    return (
      <Box>
        {data.students.map((student) => {
          const isExpanded = expandedStudent === student.id;
          const avg = data.averages[String(student.id)];
          return (
            <Card key={student.id} sx={{ mb: 1.5 }}>
              <CardContent
                sx={{ cursor: 'pointer', pb: isExpanded ? 1 : undefined, '&:last-child': { pb: isExpanded ? 1 : undefined } }}
                onClick={() => setExpandedStudent(isExpanded ? null : student.id)}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>{student.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('gradebook.average', 'Average')}: {avg != null ? `${avg}%` : '--'}
                    </Typography>
                  </Box>
                  <IconButton size="small" aria-label={isExpanded ? t('gradebook.collapse', 'Collapse') : t('gradebook.expand', 'Expand')}>
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
              </CardContent>
              <Collapse in={isExpanded}>
                <Box sx={{ px: 2, pb: 2 }}>
                  {data.assignments.map((assignment) => {
                    const cell = data.scores[String(student.id)]?.[assignment.id];
                    return (
                      <Box
                        key={assignment.id}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 0.75,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          cursor: 'pointer',
                        }}
                        onClick={() => cell && handleCellClick(assignment, cell)}
                        role="button"
                        tabIndex={0}
                        aria-label={`${assignment.title}: ${cell?.score != null ? `${cell.score}%` : '--'}`}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                          {assignment.type === 'quiz' ? <QuizIcon fontSize="small" color="action" /> : <DocumentIcon fontSize="small" color="action" />}
                          <Typography variant="body2" noWrap>{assignment.title}</Typography>
                        </Box>
                        <Box sx={{ ml: 1, flexShrink: 0 }}>
                          {getScoreChip(cell, assignment.format)}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Collapse>
            </Card>
          );
        })}
      </Box>
    );
  };

  // Desktop table view
  const renderDesktopView = () => {
    if (!data) return null;
    return (
      <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 340px)', overflow: 'auto' }}>
        <Table stickyHeader size="small" aria-label={t('gradebook.tableAriaLabel', 'Student grades matrix')}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                  backgroundColor: 'background.paper',
                  fontWeight: 700,
                  minWidth: 180,
                }}
              >
                {t('gradebook.student', 'Student')}
              </TableCell>
              {data.assignments.map((a) => (
                <TableCell
                  key={a.id}
                  align="center"
                  sx={{ fontWeight: 600, minWidth: 120, whiteSpace: 'nowrap' }}
                >
                  <Tooltip title={a.due_date ? `${t('gradebook.due', 'Due')}: ${new Date(a.due_date).toLocaleDateString()}` : ''}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {a.type === 'quiz' ? <QuizIcon fontSize="small" color="action" /> : <DocumentIcon fontSize="small" color="action" />}
                        <Typography variant="caption" fontWeight={600} sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.title}
                        </Typography>
                      </Box>
                      {a.due_date && (
                        <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                          {new Date(a.due_date).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                </TableCell>
              ))}
              <TableCell
                align="center"
                sx={{ fontWeight: 700, minWidth: 90, backgroundColor: 'action.hover' }}
              >
                {t('gradebook.average', 'Average')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.students.map((student) => {
              const avg = data.averages[String(student.id)];
              return (
                <TableRow key={student.id} hover>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      backgroundColor: 'background.paper',
                      fontWeight: 500,
                    }}
                  >
                    {student.name}
                  </TableCell>
                  {data.assignments.map((assignment) => {
                    const cell = data.scores[String(student.id)]?.[assignment.id];
                    const timeStr = cell?.time_seconds ? formatTime(cell.time_seconds) : null;
                    return (
                      <TableCell
                        key={assignment.id}
                        align="center"
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                        onClick={() => cell && handleCellClick(assignment, cell)}
                        role="button"
                        tabIndex={0}
                        aria-label={`${student.name}, ${assignment.title}: ${cell?.score != null ? `${cell.score}%` : '--'}`}
                      >
                        <Box>
                          {getScoreChip(cell, assignment.format)}
                          {timeStr && (
                            <Typography variant="caption" display="block" color="text.secondary" fontSize="0.65rem">
                              {timeStr}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    );
                  })}
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 600, backgroundColor: 'action.hover' }}
                  >
                    {avg != null ? (
                      <Chip
                        label={`${avg}%`}
                        size="small"
                        color={avg >= 70 ? 'success' : avg >= 50 ? 'warning' : 'error'}
                        sx={{ fontWeight: 700 }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">--</Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      {/* Class Selector + Export */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="gradebook-class-label">{t('gradebook.selectClass', 'Select Class')}</InputLabel>
          <Select
            labelId="gradebook-class-label"
            value={selectedClassId}
            label={t('gradebook.selectClass', 'Select Class')}
            onChange={(e) => setSelectedClassId(e.target.value as number)}
            disabled={classesLoading}
            aria-label={t('gradebook.selectClass', 'Select Class')}
          >
            {classes.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {data && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExportCsv}
            aria-label={t('gradebook.exportCsv', 'Export CSV')}
          >
            {t('gradebook.exportCsv', 'Export CSV')}
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Row */}
      {data && stats && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Paper sx={{ px: 2, py: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 100 }}>
            <Typography variant="caption" color="text.secondary">{t('gradebook.classAverage', 'Class Average')}</Typography>
            <Typography variant="h6" fontWeight={700}>
              {stats.classAvg != null ? `${stats.classAvg}%` : '--'}
            </Typography>
          </Paper>
          <Paper sx={{ px: 2, py: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 100 }}>
            <Typography variant="caption" color="text.secondary">{t('gradebook.totalAssignments', 'Assignments')}</Typography>
            <Typography variant="h6" fontWeight={700}>{stats.totalAssignments}</Typography>
          </Paper>
          <Paper sx={{ px: 2, py: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 100 }}>
            <Typography variant="caption" color="text.secondary">{t('gradebook.completionRate', 'Completion')}</Typography>
            <Typography variant="h6" fontWeight={700}>
              {stats.completionRate != null ? `${stats.completionRate}%` : '--'}
            </Typography>
          </Paper>
          <Paper sx={{ px: 2, py: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 100 }}>
            <Typography variant="caption" color="text.secondary">{t('gradebook.students', 'Students')}</Typography>
            <Typography variant="h6" fontWeight={700}>{data.students.length}</Typography>
          </Paper>
        </Box>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* No class selected */}
      {!selectedClassId && !loading && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary">
            {t('gradebook.noClassSelected', 'Select a class to view the gradebook.')}
          </Typography>
        </Box>
      )}

      {/* Empty state */}
      {data && data.assignments.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary">
            {t('gradebook.noAssignments', 'No assignments found for this class.')}
          </Typography>
        </Box>
      )}

      {/* Gradebook Matrix */}
      {data && data.assignments.length > 0 && !loading && (
        isMobile ? renderMobileView() : renderDesktopView()
      )}

      {/* Document Submissions Grading Dialog */}
      <Dialog
        open={submissionsOpen}
        onClose={() => setSubmissionsOpen(false)}
        maxWidth="md"
        fullWidth
        aria-label={t('gradebook.submissionsDialog', 'Grade submissions')}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {selectedDocumentName}
          <IconButton onClick={() => setSubmissionsOpen(false)} aria-label={t('common.close', 'Close')}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedAssignmentId && (
            <SubmissionsGrading
              assignmentId={selectedAssignmentId}
              documentName={selectedDocumentName}
              onClose={() => {
                setSubmissionsOpen(false);
                if (selectedClassId) fetchGradebook(selectedClassId);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Quiz Grading Dialog */}
      <QuizGradingDialog
        open={quizGradingOpen}
        onClose={() => {
          setQuizGradingOpen(false);
          if (selectedClassId) fetchGradebook(selectedClassId);
        }}
        quizAssignmentId={selectedQuizAssignmentId}
        quizTitle={selectedQuizTitle}
      />
    </Box>
  );
};

export default Gradebook;
